import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import { useSearchParams } from "react-router-dom";
import FilePreview from "../Common/FilePreview";
import SignatureModal from "../Common/SignatureModal";
import CanAccess from "../../../routes/CanAccess";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

// ==========================================================================
// COMPONENT → SHOW EACH ITEM WITH TIMELINE REMAINING LOGIC
// ==========================================================================
const getDocumentId = (value) => {
 if (typeof value === "string") return value;
 return value?._id || value?.id || "";
};

const ItemWithRemaining = ({ item, workOrder, indentId }) => {
 const [boqData, setBoqData] = useState(null);
 const [usedQtyBefore, setUsedQtyBefore] = useState(0);
 const workOrderId = getDocumentId(workOrder);
 const boqItemId = getDocumentId(item.boqRefLineItem);

 useEffect(() => {
 if (boqItemId && workOrderId) fetchData();
 }, [boqItemId, workOrderId]);

 const fetchData = async () => {
 try {
 // -----------------------------
 // 1 Fetch BOQ List
 // -----------------------------
 const boqRes = await api.get(`/work-orders/${workOrderId}/boq`);

 const list = boqRes.data?.data || [];

 const selected = list.find((b) => String(b._id) === String(boqItemId));
 setBoqData(selected);

 // -----------------------------
 // 2 Fetch USED QTY before this indent
 // -----------------------------
 const usedRes = await api.get(
 `/indents/by-boq/${boqItemId}/upto/${indentId}`,
 );

 setUsedQtyBefore(usedRes.data.usedQty || 0);
 } catch (err) {
 console.log("Error fetching BOQ data:", err);
 }
 };

 const remaining = boqData ? boqData.quantity - usedQtyBefore : null;
 const exceeded = remaining < 0;

  return (
    <li className="mb-3 pb-2 border-bottom">
      <div>
        <strong>{item.itemName}</strong> — {item.quantityRequested} {item.unit}
        {item.isNonBillable ? (
          <Badge bg="warning" text="dark" className="ms-2 rounded-pill" style={{ fontSize: "0.7rem" }}>
            Non-Billable
          </Badge>
        ) : (
          <Badge bg="success" className="ms-2 rounded-pill" style={{ fontSize: "0.7rem" }}>
            Billable
          </Badge>
        )}
        {item.remarks && (
          <span className="text-muted"> (Remarks: {item.remarks})</span>
        )}
      </div>

      {item.subItems && item.subItems.length > 0 && (
        <div className="mt-2 ms-3 p-2 bg-light rounded border">
          <small className="text-muted fw-bold d-block mb-1">Sub-Items Breakdown:</small>
          <ul className="list-unstyled mb-0 ps-2" style={{ fontSize: "0.82rem" }}>
            {item.subItems.map((s, idx) => (
              <li key={idx} className="text-secondary">
                • <strong>{s.name}</strong> — {s.quantity} {s.unit || item.unit || ""}
                {s.remarks ? <span className="text-muted"> ({s.remarks})</span> : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {boqData && (
        <div className="mt-1 ps-3">
          <small>
            BOQ Total:{" "}
            <strong>
              {boqData.quantity} {boqData.unit}
            </strong>
          </small>
          <br />
          <small>
            Used till this indent: <strong>{usedQtyBefore}</strong>
          </small>
          <br />
          <small
            className={
              exceeded ? "text-danger fw-bold" : "text-success fw-bold"
            }
          >
            Remaining at this time: {remaining}
          </small>
          {exceeded && <small className="text-danger ms-2">(Exceeded)</small>}
        </div>
      )}
    </li>
  );
};

// ==========================================================================
// MAIN COMPONENT
// ==========================================================================
const IndentList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [searchParams] = useSearchParams();
 const [showModal, setShowModal] = useState(false);
 const [selectedIndent, setSelectedIndent] = useState(null);
 const [indents, setIndents] = useState([]);
 const [loading, setLoading] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [approveId, setApproveId] = useState(null);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 const [filters, setFilters] = usePersistentFilters("indents_filters", {
 search: "",
 status: "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 });

 const workOrderFromUrl = searchParams.get("workOrder");
 const projectFromUrl = searchParams.get("project");

 useEffect(() => {
 fetchIndents();
 }, [
 filters.page,
 filters.limit,
 filters.search,
 filters.status,
 filters.fromDate,
 filters.toDate,
 workOrderFromUrl,
 projectFromUrl,
 ]);

 const fetchIndents = async () => {
 try {
 setLoading(true);

 const queryParams = new URLSearchParams();
 if (workOrderFromUrl && /^[0-9a-fA-F]{24}$/.test(workOrderFromUrl)) {
 queryParams.append("workOrder", workOrderFromUrl);
 }
 if (filters.search) queryParams.append("search", filters.search);
 if (filters.status) queryParams.append("status", filters.status);
 if (filters.fromDate) queryParams.append("fromDate", filters.fromDate);
 if (filters.toDate) queryParams.append("toDate", filters.toDate);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);

 const res = await api.get(`/indents?${queryParams.toString()}`);

 if (res.data.success) {
 setIndents(res.data.data || []);
 if (res.data.pagination) {
 setPagination(prev => ({
 ...prev,
 ...res.data.pagination
 }));
 }
 }
 } catch (err) {
 console.error("Indent fetch error:", err);
 swal("Error", "Failed to fetch indents", "error");
 } finally {
 setLoading(false);
 }
 };

 const handlePageChange = (page) => {
 setFilters(prev => ({ ...prev, page: page }));
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/indents/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `INDENT-${id}.pdf`;
 a.click();
 } catch (err) {
 console.error("Indent PDF download error:", err);
 swal("Error", "Failed to download Indent PDF", "error");
 }
 };

 const handleDownloadExcel = async (indent) => {
 try {
 const id = indent._id || indent;
 const res = await api.get(`/indents/${id}/excel`, {
 responseType: "blob",
 });

 const cleanIndentNo = (indent.indentNumber || `INDENT-${id}`).replace(/[^a-z0-9]/gi, "_");
 const url = window.URL.createObjectURL(new Blob([res.data], {
 type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 }));
 const a = document.createElement("a");
 a.href = url;
 a.download = `${cleanIndentNo}_Items.xlsx`;
 a.click();
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error("Indent Excel download error:", err);
 swal("Error", "Failed to download Indent Excel", "error");
 }
 };
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/indents/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("Indent PDF preview error:", err);
 swal("Error", "Failed to preview Indent PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This indent will be deleted permanently!",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/indents/${id}`);
 if (res.data.success) {
 swal("Deleted!", "Indent deleted successfully", "success");
 fetchIndents();
 }
 } catch (err) {
 swal("Error", "Failed to delete indent", "error");
 }
 };

 const handleView = async (id) => {
 try {
 const res = await api.get(`/indents/${id}`);
 if (res.data.success) {
 setSelectedIndent(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 swal("Error", "Failed to load indent details", "error");
 }
 };

 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve Indent?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.patch(`/indents/${approveId}/approve`, formData, {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 });

 if (res.data.success) {
 swal("Approved!", "Indent approved successfully", "success");
 setShowSignModal(false);
 setApproveId(null);
 fetchIndents();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve indent",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/indents/${id}/reject`, { remarks });
 if (res.data.success) {
 swal("Rejected!", "Indent rejected successfully", "success");
 fetchIndents();
 }
 } catch (err) {
 swal("Error", "Failed to reject", "error");
 }
 };

 const formatDate = (date) => {
 if (!date) return "---";
 return new Date(date).toLocaleDateString("en-US", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Approved":
 return "success";
 case "Pending":
 return "warning";
 case "Rejected":
 return "danger";
 case "Procured":
 return "info";
 case "Partially Procured":
 return "primary";
 default:
 return "secondary";
 }
 };
 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );
 return (
 <Fragment>
 <PageTitle activeMenu="Material Indent" motherMenu="Procurement" />

 {/* ====================== TABLE ====================== */}
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Material Indent List</Card.Title>
 <PermissionWrapper user={user} required={["create:indents"]}>
 <Link
 to={
 workOrderFromUrl
 ? `/indent/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/indent/add"
 }
 className="btn btn-primary"
 >
 + Add Indent
 </Link>
 </PermissionWrapper>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: "180px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search Indent</label>
 <input
 type="text"
 className="form-control form-control-sm"
 placeholder="Search Indent..."
 value={filters.search}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 search: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "140px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <select
 className="form-select form-select-sm"
 value={filters.status}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Statuses</option>
 <option value="Pending">Pending</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </select>
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading...</p>
 </div>
 ) : indents.length === 0 ? (
 <div className="text-center py-5">
 <p>No indents found.</p>
 <Link
 to={
 workOrderFromUrl
 ? `/indent/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/indent/add"
 }
 className="btn btn-primary"
 >
 + Add Indent
 </Link>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover>
 <thead>
 <tr>
 <th>#</th>
 <th>Indent Number</th>
 <th>Items</th>
 <th>Project</th>
 <th>Status</th>
 <th>Date</th>
 <th className="text-center">Signed Copy</th>
 <th>Actions</th>
 </tr>
 </thead>

 <tbody>
 {indents.map((indent, i) => (
 <tr key={indent._id}>
 <td>{i + 1}</td>
 <td>{indent.indentNumber}</td>

 <td style={{ minWidth: "300px", maxWidth: "450px" }}>
 <div style={{ 
 maxHeight: "120px", 
 overflowY: "auto", 
 paddingRight: "8px",
 scrollbarWidth: "thin" 
 }}>
 <ul className="list-unstyled mb-0">
 {indent.items?.map((it, idx) => (
 <li key={idx} className="mb-2 border-bottom pb-1 last-child-no-border">
 <div className="d-flex justify-content-between align-items-start">
 <div style={{ flex: 1 }}>
 <span style={{ fontSize: "0.9rem", fontWeight: "500", display: "block", lineHeight: "1.2" }}>
 {it.itemName}
 </span>
 {it.isNonBillable ? (
 <Badge bg="warning" text="dark" className="mt-1 rounded-pill" style={{ fontSize: "0.7rem" }}>
 Non-Billable
 </Badge>
 ) : (
 <Badge bg="success" className="mt-1 rounded-pill" style={{ fontSize: "0.7rem" }}>
 Billable
 </Badge>
 )}
 </div>
 <Badge bg="info" className="ms-2 rounded-pill" style={{ fontSize: "0.75rem" }}>
 {it.quantityRequested} {it.unit}
 </Badge>
 </div>
 </li>
 ))}
 </ul>
 </div>
 </td>

 <td>
 {indent.project?.projectName || indent.project?.projectCode || "—"}
 </td>

 <td>
 <Badge bg={getStatusBadge(indent.status)} className="rounded-pill">
 {indent.status}
 </Badge>
 </td>

 <td>{formatDate(indent.createdAt)}</td>

 <td className="text-center">
 <VerifiedCopyActions
 id={indent._id}
 type="indents"
 hasVerifiedCopy={Boolean(indent.verifiedCopy?.filePath || indent.verifiedCopy?.fileUrl || indent.signedCopy?.filePath || indent.signedCopy?.fileUrl)}
 verifiedCopy={indent.verifiedCopy || indent.signedCopy}
 onUploadSuccess={fetchIndents}
 canUpload={true}
 />
 </td>

 <td>
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn i-false p-0 cursor-pointer"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:indents"]}>
 <Dropdown.Item
 onClick={() => handleView(indent._id)}
 >
 <i className="fas fa-eye me-2"></i>View
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:indents"]}>
 <Dropdown.Item
 as={Link}
 to={
 workOrderFromUrl
 ? `/indent/edit/${indent._id}?${
 projectFromUrl
 ? `project=${projectFromUrl}&`
 : ""
 }workOrder=${workOrderFromUrl}`
 : `/indent/edit/${indent._id}`
 }
 >
 <i className="fas fa-edit me-2"></i>Edit
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:indents"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(indent._id)}
 disabled={previewingPdf}
 >
 <i className="fas fa-eye me-2 text-primary"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handleDownloadPDF(indent._id)}
 >
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handleDownloadExcel(indent)}
 >
 <i className="fas fa-file-excel me-2 text-success"></i>
 Download Excel
 </Dropdown.Item>
 </PermissionWrapper>

 {indent.status === "Pending" && (
 <>
 <PermissionWrapper user={user} required={["approve:indents"]}>
 <Dropdown.Item
 onClick={() => handleApprove(indent._id)}
 className="text-success"
 >
 <i className="fas fa-check me-2"></i>Approve
 </Dropdown.Item>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["reject:indents"]}>
 <Dropdown.Item
 onClick={() => handleReject(indent._id)}
 className="text-danger"
 >
 <i className="fas fa-times me-2"></i>Reject
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}

 <Dropdown.Divider />

 <PermissionWrapper user={user} required={["delete:indents"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(indent._id)}
 >
 <i className="fas fa-trash me-2"></i>Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={filters.page}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={indents.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for Indent Approval"
 />

 {/* ====================== DETAILS MODAL ====================== */}
 <Modal
 show={showModal}
 onHide={() => setShowModal(false)}
 centered
 size="lg"
 >
 <Modal.Header closeButton>
 <Modal.Title>Indent Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedIndent && (
 <>
 <p>
 <strong>Indent Number:</strong> {selectedIndent.indentNumber}
 </p>
 <p>
 <strong>Project:</strong> {selectedIndent.project?.project}
 </p>

 <p>
 <strong>Status:</strong>
 <Badge
 bg={getStatusBadge(selectedIndent.status)}
 className="ms-2 rounded-pill"
 >
 {selectedIndent.status}
 </Badge>
 </p>

 <hr />

 <h5>Items</h5>
 <ul className="ps-3">
 {selectedIndent.items?.map((it, i) => (
 <ItemWithRemaining
 key={i}
 item={it}
 workOrder={selectedIndent.workOrder}
 indentId={selectedIndent._id}
 />
 ))}
 </ul>

 <hr />

 {selectedIndent.remarks && (
 <>
 <h5>Overall Remarks</h5>
 <p className="text-muted">{selectedIndent.remarks}</p>
 </>
 )}
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 {/* =========================
 PDF PREVIEW LOADER
========================== */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Indent PDF…</h6>
 </div>
 </div>
 )}
 </Fragment>
 );
};

export default IndentList;
